import { Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import colors from "app/colors";
import { useAuth } from "modules/providers/auth-provider";
import { IS_APPLE } from "modules/utils/Constants";
import useUserPreference, {
  EnterKeySettingValue,
} from "modules/hooks/use-user-preference";

interface IProps {
  onEnterKeySettingChange?: (enterKeySetting: EnterKeySettingValue) => void;
}

const EnterKeySettings = ({ onEnterKeySettingChange }: IProps) => {
  const { me } = useAuth();
  const { stringValue: enterKeySetting, upsertUserPreference } =
    useUserPreference({
      userId: me?.id,
      name: "ENTER_KEY_SETTING",
    });

  const [localEnterKeySetting, setLocalEnterKeySetting] = useState<
    EnterKeySettingValue | undefined
  >(enterKeySetting as EnterKeySettingValue);

  useEffect(() => {
    // API values trump local values
    setLocalEnterKeySetting(enterKeySetting as EnterKeySettingValue);
  }, [enterKeySetting]);

  const _onEnterKeySettingChange = useCallback(
    (enterKeySetting: EnterKeySettingValue) => {
      setLocalEnterKeySetting(enterKeySetting);

      // Allows the UI to be snappy
      setTimeout(() => {
        upsertUserPreference(enterKeySetting);
      }, 0);

      onEnterKeySettingChange?.(enterKeySetting);
    },
    [onEnterKeySettingChange, upsertUserPreference],
  );

  return (
    <Stack color={colors.gray50} spacing={1}>
      <Text fontSize="sm">
        When typing a message, pressing the <Hotkey>Enter</Hotkey> key
      </Text>
      <RadioGroup
        value={localEnterKeySetting}
        onChange={_onEnterKeySettingChange}
        pt={1}
        colorScheme="gray"
      >
        <Stack>
          <Radio value="send-message">
            <Text fontSize="sm">Sends the message</Text>
          </Radio>
          <Radio value="new-line">
            <Text fontSize="sm">
              Adds a new line (use <Hotkey>{IS_APPLE ? "⌘" : "Ctrl"}</Hotkey> +{" "}
              <Hotkey>Enter</Hotkey> to send the message)
            </Text>
          </Radio>
        </Stack>
      </RadioGroup>
    </Stack>
  );
};

export default memo(EnterKeySettings);

const Hotkey = ({ children }: { children: ReactNode }) => (
  <Text
    as="span"
    p={1}
    borderWidth={1}
    borderColor={colors.gray50}
    rounded="sm"
    fontSize="2xs"
  >
    {children}
  </Text>
);
